import { ModalBackdrop } from './ModalFrame';
import { useRememberedSection } from './navigation-preferences';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ClientDirectoryRequestError,
  clientDirectoryApi,
  type ClientDirectory,
  type ClientFields,
  type ClientRecord,
  type ClientSource,
} from './client-directory-api';
import { partyDirectoryApi, type CommercialSummary } from './party-directory-api';
import { ClientContacts } from './ClientContacts';
import { CommercialStatistics } from './CommercialStatistics';
import { PrototypeIcon } from './PrototypeIcon';
import { ClientWebsitesFields, clientWebsiteUrls } from './ClientWebsitesFields';
import { ClientActivityOverview } from './ClientActivityOverview';

const EMPTY_FIELDS: ClientFields = {
  stage: 'CLIENT',
  displayName: '',
  email: null,
  siren: null,
  siret: null,
  phone: null,
  websiteUrl: null,
  websiteUrls: [],
  postalAddress: null,
  notes: null,
};
type Screen = 'overview' | 'clients' | 'prospects' | 'historical' | 'detail';
type WizardStep = 'source' | 'pick' | 'link' | 'identity' | 'confirm' | 'logo';
type DuplicateReview = {
  candidates: readonly { id: string; displayName: string }[];
  execute: (ids: readonly string[]) => Promise<void>;
};

function errorLabel(error: unknown): string {
  if (error instanceof ClientDirectoryRequestError) {
    if (error.code === 'HOSTING_SITE_ASSIGNED')
      return 'Ce Client est rattaché à un site enregistré. Retirez ce rattachement avant de le reclasser en Prospect.';
    if (error.code === 'HOSTING_SERVER_OWNER')
      return 'Ce Client possède un serveur enregistré. Il ne peut pas être reclassé en Prospect.';
    if (error.code === 'STALE_RECORD')
      return 'Cette fiche a changé. Actualisez avant de réessayer.';
    if (error.code === 'SOURCE_ALREADY_LINKED') return 'Cette référence Freebe est déjà rattachée.';
    if (error.code === 'GROUP_ALREADY_LINKED')
      return 'Ce destinataire de devis est déjà lié à un Client.';
    if (error.code === 'DUPLICATE_REVIEW')
      return 'Une fiche similaire existe déjà. Vérifiez-la avant de confirmer la promotion.';
    if (error.code === 'CLIENT_RECORD_INVALID') return 'Vérifiez les informations saisies.';
  }
  return 'Impossible de terminer cette action. Réessayez après actualisation.';
}

/** L'annuaire métier est la page principale. La bibliothèque Freebe et les décisions statistiques
 * restent des parcours de sélection distincts : une suggestion n'est jamais une fiche ni un accès. */
export function ClientDirectoryPanel({
  csrfToken,
  initialClient = null,
  initialScreen = 'overview',
}: {
  readonly csrfToken: string;
  readonly initialClient?: ClientRecord | null;
  readonly initialScreen?: 'overview' | 'clients' | 'prospects';
}): React.JSX.Element {
  const [listScreen, setListScreen] = useRememberedSection<Exclude<Screen, 'detail'>>(
    'client-view',
    initialScreen,
    ['overview', 'clients', 'prospects', 'historical'],
    initialScreen !== 'overview',
  );
  const [detailOpen, setDetailOpen] = useState(initialClient !== null);
  const screen: Screen = detailOpen ? 'detail' : listScreen;
  const setScreen = (next: Screen): void => {
    setDetailOpen(next === 'detail');
    if (next !== 'detail') setListScreen(next);
  };
  const [linkedTarget, setLinkedTarget] = useState<ClientRecord | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [directory, setDirectory] = useState<ClientDirectory | null>(null);
  const [totals, setTotals] = useState<ClientDirectory | null>(null);
  const [summary, setSummary] = useState<CommercialSummary | null>(null);
  const [prospectQuery, setProspectQuery] = useState('');
  const [prospectPage, setProspectPage] = useState(0);
  const [showQualification, setShowQualification] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientRecord | null>(initialClient);
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [confirmation, setConfirmation] = useState<readonly ClientRecord[] | null>(null);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [wizard, setWizard] = useState<WizardStep | null>(null);
  const [source, setSource] = useState<ClientSource | null>(null);
  const [prospectGroupId, setProspectGroupId] = useState<string | null>(null);
  const [attachTarget, setAttachTarget] = useState<ClientRecord | null>(null);
  const [groupAttachTarget, setGroupAttachTarget] = useState<ClientRecord | null>(null);
  const [fields, setFields] = useState<ClientFields>(EMPTY_FIELDS);
  const [editing, setEditing] = useState<ClientRecord | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [relationshipConfirmed, setRelationshipConfirmed] = useState(false);
  const [review, setReview] = useState<DuplicateReview | null>(null);
  const searchSequence = useRef(0);
  const activeQuery =
    wizard === 'pick' || wizard === 'link'
      ? query
      : screen === 'clients' || screen === 'historical' || screen === 'overview'
        ? query
        : screen === 'prospects'
          ? prospectQuery
          : '';
  const activePage =
    wizard === null
      ? screen === 'clients' || screen === 'historical'
        ? page
        : screen === 'prospects'
          ? prospectPage
          : 0
      : 0;
  const activeMode =
    screen === 'prospects' && wizard === null
      ? 'prospects'
      : screen === 'historical' && wizard === null
        ? 'historical'
        : 'clients';

  useEffect(() => {
    const sequence = ++searchSequence.current;
    const timeout = window.setTimeout(
      () => {
        setLoading(true);
        void clientDirectoryApi.search(activeQuery, activePage, activeMode).then(
          (result) => {
            if (sequence !== searchSequence.current) return;
            setDirectory(result);
            if (!activeQuery && activePage === 0 && activeMode === 'clients') setTotals(result);
            setLoading(false);
          },
          () => {
            if (sequence !== searchSequence.current) return;
            setLoading(false);
            setError('Annuaire indisponible pour le moment.');
          },
        );
      },
      activeQuery ? 200 : 0,
    );
    return () => window.clearTimeout(timeout);
  }, [activeQuery, activePage, activeMode]);

  useEffect(() => {
    if (screen === 'overview') {
      void partyDirectoryApi.commercialSummary(0).then(setSummary, () => setSummary(null));
    }
  }, [screen]);

  useEffect(() => {
    if (screen !== 'prospects') return;
    let active = true;
    void partyDirectoryApi.commercialSummary(0).then(
      (result) => {
        if (active) setSummary(result);
      },
      () => {
        if (active) setSummary(null);
      },
    );
    return () => {
      active = false;
    };
  }, [screen, showQualification]);

  const refresh = async (): Promise<ClientDirectory> => {
    const result = await clientDirectoryApi.search(activeQuery, activePage, activeMode);
    setDirectory(result);
    void clientDirectoryApi.search('', 0).then(setTotals, () => undefined);
    void partyDirectoryApi.commercialSummary(0).then(setSummary, () => setSummary(null));
    return result;
  };

  const openClients = (): void => {
    setError(null);
    setFeedback(null);
    setScreen('clients');
    setQuery('');
    setPage(0);
    setSelectedClient(null);
    setSelectedIds([]);
  };
  const closeWizard = (): void => {
    setWizard(null);
    setEditing(null);
    setSource(null);
    setLinkedTarget(null);
    setProspectGroupId(null);
    setAttachTarget(null);
    setGroupAttachTarget(null);
    setLogoFile(null);
    setReview(null);
    setRelationshipConfirmed(false);
    setQuery('');
  };
  const startWizard = (): void => {
    setError(null);
    setFeedback(null);
    setFields(EMPTY_FIELDS);
    setWizard('source');
    setSource(null);
    setLinkedTarget(null);
    setProspectGroupId(null);
    setGroupAttachTarget(null);
    setEditing(null);
    setReview(null);
    setRelationshipConfirmed(false);
    setLogoFile(null);
    setQuery('');
  };
  const startFreebe = (): void => {
    startWizard();
    setWizard('pick');
  };
  const startFromQuoteProspect = (groupId: string, label: string | null): void => {
    startWizard();
    setProspectGroupId(groupId);
    setFields({ ...EMPTY_FIELDS, displayName: label ?? '' });
  };
  const chooseSource = (candidate: ClientSource): void => {
    if (candidate.linkedClient) {
      const linked = candidate.linkedClient;
      if (linked.stage === 'CLIENT' && linked.origin !== 'FREEBE_LEGACY_UNREVIEWED') {
        if (prospectGroupId) {
          setGroupAttachTarget(linked);
          setRelationshipConfirmed(false);
          setWizard('confirm');
        } else openClient(linked);
        return;
      }
      setSource(candidate);
      setLinkedTarget(linked);
      setFields({ ...linked, stage: 'CLIENT' });
      setRelationshipConfirmed(false);
      setWizard('confirm');
      return;
    }
    setLinkedTarget(null);
    setSource(candidate);
    setReview(null);
    setFields({
      ...EMPTY_FIELDS,
      displayName: candidate.displayName,
      email: candidate.email,
      siren: candidate.siren,
      siret: candidate.siret,
    });
    setWizard('identity');
  };
  const openClient = (client: ClientRecord): void => {
    setError(null);
    setFeedback(null);
    closeWizard();
    setSelectedClient(client);
    setScreen('detail');
  };
  const run = async (
    action: (ids: readonly string[]) => Promise<ClientRecord>,
    ids: readonly string[] = [],
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const saved = await action(ids);
      let current = saved;
      const replacedLogo = wizard === 'logo';
      if (logoFile && !attachTarget && wizard !== 'logo') {
        try {
          current = await clientDirectoryApi.saveLogo(saved, logoFile, csrfToken);
        } catch {
          setError(
            'Fiche enregistrée, mais le logo n’a pas été ajouté. Réessayez depuis la fiche.',
          );
        }
      }
      closeWizard();
      setSelectedClient(current);
      setScreen('detail');
      if (replacedLogo) setFeedback('Logo du client enregistré.');
      try {
        await refresh();
      } catch {
        setError('La fiche est enregistrée. La liste sera actualisée au prochain chargement.');
      }
    } catch (failure) {
      if (
        failure instanceof ClientDirectoryRequestError &&
        failure.code === 'DUPLICATE_REVIEW' &&
        !linkedTarget
      ) {
        setReview({
          candidates: failure.candidates,
          execute: async (confirmed) => run(action, confirmed),
        });
      } else setError(errorLabel(failure));
    } finally {
      setBusy(false);
    }
  };
  const save = (): void => {
    if (linkedTarget) {
      void run(async () => {
        const confirmed = await clientDirectoryApi.confirmLegacy(linkedTarget, csrfToken);
        return prospectGroupId
          ? clientDirectoryApi.attachRecipientGroup(confirmed, prospectGroupId, csrfToken)
          : confirmed;
      });
    } else if (editing) {
      void run((ids) => clientDirectoryApi.update(editing, fields, csrfToken, ids));
    } else if (groupAttachTarget && prospectGroupId) {
      void run(() =>
        clientDirectoryApi.attachRecipientGroup(groupAttachTarget, prospectGroupId, csrfToken),
      );
    } else if (attachTarget && source) {
      void run(() => clientDirectoryApi.attach(attachTarget, source.id, csrfToken));
    } else if (source) {
      void run((ids) =>
        clientDirectoryApi.createFromFreebe(source.id, fields, csrfToken, ids, prospectGroupId),
      );
    } else {
      void run((ids) => clientDirectoryApi.createManual(fields, csrfToken, ids, prospectGroupId));
    }
  };
  const changeField = (key: keyof ClientFields, value: string): void => {
    if (key === 'stage') return;
    setReview(null);
    setFields((current) => ({ ...current, [key]: value || null }));
  };
  const editClient = (client: ClientRecord): void => {
    setEditing(client);
    setFields({
      stage: 'CLIENT',
      displayName: client.displayName,
      email: client.email,
      siren: client.siren,
      siret: client.siret,
      phone: client.phone,
      websiteUrl: client.websiteUrl,
      websiteUrls: clientWebsiteUrls(client),
      postalAddress: client.postalAddress,
      notes: client.notes,
    });
    setWizard('identity');
    setSource(null);
    setLinkedTarget(null);
    setLogoFile(null);
    setRelationshipConfirmed(true);
    setReview(null);
  };
  const attach = (client: ClientRecord): void => {
    setAttachTarget(client);
    setSource(null);
    setLinkedTarget(null);
    setQuery('');
    setWizard('pick');
  };
  const requalify = async (): Promise<void> => {
    if (!confirmation || !confirmationChecked) return;
    setBusy(true);
    setError(null);
    try {
      const results = await Promise.allSettled(
        confirmation.map((record) => clientDirectoryApi.requalify(record, 'PROSPECT', csrfToken)),
      );
      const failed = results.filter((result) => result.status === 'rejected');
      if (failed.length) {
        setError(
          `${failed.length} fiche(s) n’ont pas pu être corrigées. Actualisez la liste et réessayez.`,
        );
      }
      setSelectedClient(null);
      setSelectedIds([]);
      setConfirmation(null);
      setConfirmationChecked(false);
      setScreen('clients');
      setQuery('');
      setPage(0);
      setProspectQuery('');
      setProspectPage(0);
      const [next, stats] = await Promise.all([
        clientDirectoryApi.search('', 0, 'clients'),
        partyDirectoryApi.commercialSummary(0),
      ]);
      setDirectory(next);
      setSummary(stats);
      void clientDirectoryApi.search('', 0).then(setTotals, () => undefined);
    } catch (failure) {
      setError(errorLabel(failure));
    } finally {
      setBusy(false);
    }
  };
  const requestRequalification = (records: readonly ClientRecord[]): void => {
    setError(null);
    setConfirmationChecked(false);
    setConfirmation(records);
  };

  return (
    <div className="client-directory">
      {feedback && wizard === null && (
        <p
          role="status"
          className="client-directory__success client-directory__alert tosho-feedback is-success"
        >
          {feedback}
        </p>
      )}
      {error && wizard === null && (
        <p
          role="alert"
          className="client-directory__error client-directory__alert tosho-feedback is-error"
        >
          {error}
        </p>
      )}
      {screen === 'overview' && wizard === null && (
        <>
          <header className="client-directory__heading">
            <div>
              <p className="client-directory__eyebrow">Répertoire central</p>
              <h1>Clients &amp; prospects</h1>
              <p>Une identité client unique, enrichie à chaque import Freebe.</p>
            </div>
            <button type="button" className="client-directory__primary" onClick={startWizard}>
              ＋ Ajouter un client
            </button>
          </header>
          <div className="client-directory__overview-cards">
            <button type="button" className="client-directory__metric" onClick={openClients}>
              <PrototypeIcon name="users-round" />
              <strong>{totals?.clientCount ?? summary?.clientCount ?? '—'}</strong>
              <span>Clients</span>
            </button>
            <button
              type="button"
              className="client-directory__metric"
              onClick={() => setScreen('prospects')}
            >
              <PrototypeIcon name="user-round-help" />
              <strong>{totals?.sourceCount ?? '—'}</strong>
              <span>Prospects</span>
            </button>
            <button
              type="button"
              className="client-directory__metric"
              onClick={() => {
                setScreen('historical');
                setQuery('');
                setPage(0);
                setSelectedIds([]);
              }}
            >
              <strong>{totals?.historicalCount ?? '—'}</strong>
              <span>Historiques</span>
            </button>
            <div className="client-directory__import-note">
              <PrototypeIcon name="database" />
              <span>
                <strong>Dernier import Freebe</strong>
                <small>
                  {totals?.lastImportedAt
                    ? new Date(totals.lastImportedAt).toLocaleString('fr-FR', {
                        dateStyle: 'long',
                        timeStyle: 'short',
                      })
                    : 'Aucun import'}
                </small>
              </span>
              <button type="button" onClick={startFreebe}>
                <PrototypeIcon name="search" /> Choisir une référence
              </button>
            </div>
          </div>
          <section className="client-directory__table-card" aria-label="Aperçu des clients">
            <div className="client-directory__table-toolbar">
              <h2>Clients</h2>
              <Search
                value={query}
                onChange={(value) => {
                  setQuery(value);
                  setPage(0);
                  setSelectedIds([]);
                }}
                placeholder="Rechercher un client, domaine, e-mail ou SIREN"
              />
            </div>
            <ClientRows clients={directory?.clients ?? []} onOpen={openClient} />
          </section>
        </>
      )}

      {(screen === 'clients' || screen === 'historical') && wizard === null && (
        <>
          <Back
            onClick={() => {
              setScreen('overview');
              setQuery('');
              setPage(0);
            }}
          />
          <header className="client-directory__heading">
            <div>
              <p className="client-directory__eyebrow">Répertoire central</p>
              <h1>{screen === 'historical' ? 'Clients historiques' : 'Clients'}</h1>
              <p>{directory?.clientCount ?? '—'} fiches dans l’organisation.</p>
            </div>
            <button type="button" className="client-directory__primary" onClick={startWizard}>
              ＋ Ajouter un client
            </button>
          </header>
          <section className="client-directory__table-card" aria-label="Liste des clients">
            <div className="client-directory__table-toolbar">
              <Search
                value={query}
                onChange={(value) => {
                  setQuery(value);
                  setPage(0);
                  setSelectedIds([]);
                }}
                placeholder="Rechercher un client, e-mail, SIREN ou SIRET"
              />
              <span>{loading ? 'Recherche…' : `${directory?.clientCount ?? 0} résultat(s)`}</span>
            </div>
            {selectedIds.length > 0 && (
              <div className="client-directory__bulk">
                <span>{selectedIds.length} sélectionné(s)</span>
                <button
                  type="button"
                  onClick={() =>
                    requestRequalification(
                      (directory?.clients ?? []).filter((client) =>
                        selectedIds.includes(client.id),
                      ),
                    )
                  }
                >
                  <PrototypeIcon name="users-round" /> Repasser en prospect
                </button>
              </div>
            )}
            <ClientRows
              clients={directory?.clients ?? []}
              onOpen={openClient}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
            />
            {directory?.clients.length === 0 && !loading && (
              <p className="client-directory__empty">Aucun client correspondant.</p>
            )}
            <div className="client-directory__pagination">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => {
                  setPage(page - 1);
                  setSelectedIds([]);
                }}
              >
                Précédent
              </button>
              <span>Page {page + 1}</span>
              <button
                type="button"
                disabled={(page + 1) * 50 >= (directory?.clientCount ?? 0)}
                onClick={() => {
                  setPage(page + 1);
                  setSelectedIds([]);
                }}
              >
                Suivant
              </button>
            </div>
          </section>
        </>
      )}

      {screen === 'prospects' && wizard === null && (
        <>
          <Back onClick={() => setScreen('overview')} />
          <header className="client-directory__heading">
            <div>
              <p className="client-directory__eyebrow">Suivi commercial</p>
              <h1>Prospects</h1>
              <p>Références Freebe et statut de qualification.</p>
            </div>
            <button type="button" onClick={() => setShowQualification((current) => !current)}>
              {showQualification ? 'Masquer la qualification' : 'Qualifier les devis'}
            </button>
          </header>
          <section
            className="client-directory__table-card"
            aria-label="Liste des prospects identifiés"
          >
            <div className="client-directory__table-toolbar">
              <Search
                value={prospectQuery}
                onChange={(value) => {
                  setProspectQuery(value);
                  setProspectPage(0);
                }}
                placeholder="Rechercher un prospect"
              />
              <span>{directory?.sourceCount ?? 0} résultat(s)</span>
            </div>
            <div className="client-directory__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Prospect Freebe</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {directory?.freebeCandidates.map((candidate) => (
                    <tr key={candidate.id}>
                      <td>
                        <strong>{candidate.displayName}</strong>
                        <small>{candidate.email && ` · ${candidate.email}`}</small>
                      </td>
                      <td>
                        <span className="client-directory__badge">
                          {candidate.linkedClient?.stage === 'CLIENT' ? 'Client' : 'Prospect'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => {
                            if (candidate.linkedClient || !candidate.linkedClientId)
                              chooseSource(candidate);
                            else {
                              openClients();
                              setQuery(candidate.displayName);
                            }
                          }}
                        >
                          {candidate.linkedClient?.stage === 'CLIENT' &&
                          candidate.linkedClient.origin !== 'FREEBE_LEGACY_UNREVIEWED'
                            ? 'Voir la fiche'
                            : 'Qualifier comme client'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {directory?.freebeCandidates.length === 0 && (
              <p className="client-directory__empty">
                Aucun prospect identifié pour cette recherche.
              </p>
            )}
            <div className="client-directory__pagination">
              <button
                type="button"
                disabled={prospectPage === 0}
                onClick={() => setProspectPage(prospectPage - 1)}
              >
                Précédent
              </button>
              <span>Page {prospectPage + 1}</span>
              <button
                type="button"
                disabled={(prospectPage + 1) * 50 >= (directory?.sourceCount ?? 0)}
                onClick={() => setProspectPage(prospectPage + 1)}
              >
                Suivant
              </button>
            </div>
          </section>
          {!!summary?.prospects.length && (
            <section className="client-directory__table-card">
              <div className="client-directory__table-toolbar">
                <h2>Destinataires de devis à rapprocher</h2>
              </div>
              <div className="client-directory__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Destinataire</th>
                      <th>Devis</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.prospects.map((prospect) => (
                      <tr key={prospect.id}>
                        <td>
                          <strong>{prospect.label || 'Sans libellé'}</strong>
                        </td>
                        <td>{prospect.quoteCount}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => startFromQuoteProspect(prospect.id, prospect.label)}
                          >
                            Passer en Client
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          {showQualification && <CommercialStatistics csrfToken={csrfToken} />}
        </>
      )}

      {screen === 'detail' && selectedClient && wizard === null && (
        <>
          <Back onClick={openClients} label="Retour aux clients" />
          <header className="client-directory__detail-heading">
            <ClientLogo client={selectedClient} />
            <div>
              <p className="client-directory__eyebrow">Fiche client</p>
              <h1>{selectedClient.displayName}</h1>
              <p>
                {selectedClient.origin === 'FREEBE_LEGACY_UNREVIEWED'
                  ? 'Client importé · qualification à confirmer'
                  : selectedClient.sources.length
                    ? 'Freebe confirmé'
                    : 'Création manuelle'}
              </p>
            </div>
            <div className="client-directory__actions">
              <button type="button" onClick={() => editClient(selectedClient)}>
                <PrototypeIcon name="settings" /> Modifier
              </button>
              <button
                type="button"
                onClick={() => {
                  setLogoFile(null);
                  setEditing(selectedClient);
                  setWizard('logo');
                }}
              >
                <PrototypeIcon name="plus" />{' '}
                {selectedClient.hasLogo ? 'Remplacer le logo' : 'Ajouter un logo'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => requestRequalification([selectedClient])}
              >
                <PrototypeIcon name="users-round" /> Repasser en Prospect
              </button>
            </div>
          </header>
          <div className="client-directory__detail-grid">
            <section className="client-directory__table-card">
              <p className="client-directory__eyebrow">Coordonnées</p>
              <h2>Identité</h2>
              <dl className="client-directory__facts">
                <dt>E-mail</dt>
                <dd>{selectedClient.email || 'À renseigner'}</dd>
                <dt>Téléphone</dt>
                <dd>{selectedClient.phone || 'À renseigner'}</dd>
                <dt>Sites internet</dt>
                <dd>
                  {clientWebsiteUrls(selectedClient).length ? (
                    <ul className="client-directory__websites-list">
                      {clientWebsiteUrls(selectedClient).map((url) => (
                        <li key={url}>
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    'À renseigner'
                  )}
                </dd>
                <dt>Adresse</dt>
                <dd>{selectedClient.postalAddress || 'À renseigner'}</dd>
                <dt>SIREN / SIRET</dt>
                <dd>{selectedClient.siren || selectedClient.siret || 'Non disponible'}</dd>
              </dl>
              {selectedClient.notes && (
                <p className="client-directory__notes">
                  <strong>Remarque : </strong>
                  {selectedClient.notes}
                </p>
              )}
            </section>
            <section className="client-directory__table-card client-directory__provenance">
              <p className="client-directory__eyebrow">Provenance</p>
              <h2>Relation Freebe</h2>
              {selectedClient.sources.length === 0 ? (
                <p>Aucune référence associée.</p>
              ) : (
                <ul className="client-directory__source-list">
                  {selectedClient.sources.map((item) => (
                    <li key={item.id}>
                      <strong>{item.displayName}</strong>
                      <small>
                        {item.differs ? 'Coordonnées différentes de Freebe' : 'Référence liée'}
                      </small>
                    </li>
                  ))}
                </ul>
              )}
              {selectedClient.origin === 'FREEBE_LEGACY_UNREVIEWED' ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Confirmer ce lien historique et la relation Client ?'))
                      void run(() => clientDirectoryApi.confirmLegacy(selectedClient, csrfToken));
                  }}
                >
                  Confirmer comme client
                </button>
              ) : (
                <button type="button" onClick={() => attach(selectedClient)}>
                  {selectedClient.sources.length
                    ? 'Changer le rattachement Freebe'
                    : 'Rattacher une référence Freebe'}
                </button>
              )}
            </section>
          </div>
          {selectedClient.origin !== 'FREEBE_LEGACY_UNREVIEWED' && (
            <ClientContacts
              clientId={selectedClient.id}
              clientName={selectedClient.displayName}
              clientLogo={selectedClient.hasLogo}
              clientLogoVersion={selectedClient.version}
              csrfToken={csrfToken}
              onFeedback={setFeedback}
            />
          )}
          <ClientActivityOverview client={selectedClient} />
        </>
      )}

      {loading && wizard === null && (
        <p role="status" className="client-directory__quiet">
          Chargement…
        </p>
      )}

      {wizard && (
        <ModalBackdrop
          className="client-directory__overlay client-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeWizard();
          }}
          aria-label={editing ? 'Modifier le client' : 'Nouveau client'}
          close={<ModalCloseButton aria-label="Fermer" onClick={closeWizard} />}
        >
          <section className="tosho-modal-panel client-directory__modal">
            <header className="client-directory__modal-heading tosho-modal-header">
              <div>
                <h2>
                  <PrototypeIcon name="users-round" />{' '}
                  {editing
                    ? `Modifier ${editing.displayName}`
                    : attachTarget
                      ? `Rattacher à ${attachTarget.displayName}`
                      : groupAttachTarget
                        ? `Lier à ${groupAttachTarget.displayName}`
                        : 'Nouveau client'}
                </h2>
                <p>
                  {editing
                    ? 'Les corrections Tosho restent prioritaires sur les imports.'
                    : attachTarget
                      ? 'Vérifiez la référence avant de confirmer le rattachement.'
                      : 'Créez la fiche centrale du client avant ses futurs espaces.'}
                </p>
              </div>
            </header>
            {error && (
              <p
                role="alert"
                className="client-directory__error client-directory__alert tosho-feedback is-error"
              >
                {error}
              </p>
            )}
            {!editing &&
              !attachTarget &&
              !groupAttachTarget &&
              wizard !== 'logo' &&
              wizard !== 'link' && (
                <div className="client-directory__steps" aria-label="Étapes de création">
                  {['Source', 'Identité', 'Relation'].map((step, index) => (
                    <span
                      key={step}
                      className={
                        index === (wizard === 'identity' ? 1 : wizard === 'confirm' ? 2 : 0)
                          ? 'is-active'
                          : ''
                      }
                    >
                      <b>{index + 1}</b>
                      {step}
                    </span>
                  ))}
                </div>
              )}

            {wizard === 'source' && (
              <div className="client-directory__source-options">
                <button
                  type="button"
                  onClick={() => {
                    setWizard('pick');
                    setQuery('');
                  }}
                >
                  <PrototypeIcon name="cloud-upload" />
                  <strong>Depuis Freebe</strong>
                  <small>Rechercher et choisir une référence importée.</small>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSource(null);
                    setLinkedTarget(null);
                    setFields({ ...EMPTY_FIELDS, displayName: fields.displayName });
                    setWizard('identity');
                  }}
                >
                  <PrototypeIcon name="plus" />
                  <strong>Création manuelle</strong>
                  <small>Pour un client absent de Freebe ou un dossier interne.</small>
                </button>
                {prospectGroupId && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('');
                      setWizard('link');
                    }}
                  >
                    <PrototypeIcon name="users-round" />
                    <strong>Client existant</strong>
                    <small>Rechercher sa fiche et confirmer le lien avec ce destinataire.</small>
                  </button>
                )}
              </div>
            )}

            {wizard === 'link' && (
              <div className="client-directory__picker">
                <Search
                  value={query}
                  onChange={setQuery}
                  placeholder="Rechercher un Client existant"
                />
                <div className="client-directory__suggestions" role="list">
                  {directory?.clients.map((client) => (
                    <button
                      type="button"
                      role="listitem"
                      key={client.id}
                      onClick={() => {
                        setGroupAttachTarget(client);
                        setRelationshipConfirmed(false);
                        setWizard('confirm');
                      }}
                    >
                      <strong>{client.displayName}</strong>
                      <small>{client.email || 'Coordonnées non disponibles'}</small>
                      <em>Sélectionner</em>
                    </button>
                  ))}
                </div>
                {directory?.clients.length === 0 && !loading && <p>Aucun Client correspondant.</p>}
                {directory && directory.clientCount > 50 && (
                  <p className="client-directory__quiet">
                    Affichage des 50 premières fiches. Utilisez la recherche pour affiner.
                  </p>
                )}
              </div>
            )}

            {wizard === 'pick' && (
              <div className="client-directory__picker">
                <Search
                  value={query}
                  onChange={setQuery}
                  placeholder="Rechercher dans Freebe : nom, e-mail, SIREN ou SIRET"
                />
                <p>
                  {query
                    ? `${directory?.sourceCount ?? 0} résultat(s)`
                    : 'Dernières références importées dans Tosho'}
                </p>
                <div className="client-directory__suggestions" role="list">
                  {directory?.freebeCandidates.map((candidate) => (
                    <button
                      type="button"
                      role="listitem"
                      key={candidate.id}
                      disabled={Boolean(
                        candidate.linkedClientId && (attachTarget || !candidate.linkedClient),
                      )}
                      onClick={() => {
                        if (attachTarget) {
                          setSource(candidate);
                          setWizard('confirm');
                        } else chooseSource(candidate);
                      }}
                    >
                      <strong>{candidate.displayName}</strong>
                      <small>
                        {candidate.email ||
                          candidate.siret ||
                          candidate.siren ||
                          'Coordonnées non disponibles'}
                      </small>
                      <em>
                        {candidate.linkedClient
                          ? candidate.linkedClient.stage === 'CLIENT' &&
                            candidate.linkedClient.origin !== 'FREEBE_LEGACY_UNREVIEWED'
                            ? 'Voir la fiche'
                            : 'Confirmer comme Client'
                          : candidate.linkedClientId
                            ? 'Déjà lié à Tosho'
                            : 'Sélectionner'}
                      </em>
                    </button>
                  ))}
                </div>
                {directory?.freebeCandidates.length === 0 && !loading && (
                  <p>Aucune référence correspondante.</p>
                )}
                {directory && directory.sourceCount > 50 && (
                  <p className="client-directory__quiet">
                    Affichage des 50 premières références. Utilisez la recherche pour affiner.
                  </p>
                )}
              </div>
            )}

            {wizard === 'identity' && (
              <form
                id="client-identity"
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  if (editing) save();
                  else setWizard('confirm');
                }}
              >
                {source && (
                  <p className="client-directory__selected-source">
                    Référence Freebe sélectionnée : <strong>{source.displayName}</strong>{' '}
                    <button type="button" onClick={() => setWizard('pick')}>
                      Changer
                    </button>
                  </p>
                )}
                <div className="client-directory__fields">
                  <label>
                    Nom affiché dans Tosho
                    <input
                      required
                      maxLength={255}
                      value={fields.displayName}
                      onChange={(event) => changeField('displayName', event.currentTarget.value)}
                    />
                  </label>
                  <label>
                    E-mail général
                    <input
                      type="email"
                      maxLength={320}
                      value={fields.email ?? ''}
                      onChange={(event) => changeField('email', event.currentTarget.value)}
                    />
                  </label>
                  <ClientWebsitesFields
                    values={clientWebsiteUrls(fields)}
                    onChange={(websiteUrls) => {
                      setReview(null);
                      setFields((current) => ({
                        ...current,
                        websiteUrls,
                        websiteUrl: websiteUrls[0] ?? null,
                      }));
                    }}
                  />
                  <label>
                    Téléphone
                    <input
                      type="tel"
                      maxLength={32}
                      value={fields.phone ?? ''}
                      onChange={(event) => changeField('phone', event.currentTarget.value)}
                    />
                  </label>
                  <label>
                    SIREN
                    <input
                      value={fields.siren ?? ''}
                      onChange={(event) => changeField('siren', event.currentTarget.value)}
                      readOnly={!!source}
                    />
                  </label>
                  <label>
                    SIRET
                    <input
                      value={fields.siret ?? ''}
                      onChange={(event) => changeField('siret', event.currentTarget.value)}
                      readOnly={!!source}
                    />
                  </label>
                  <label className="client-directory__wide">
                    Adresse
                    <input
                      maxLength={500}
                      value={fields.postalAddress ?? ''}
                      onChange={(event) => changeField('postalAddress', event.currentTarget.value)}
                    />
                  </label>
                  <label className="client-directory__wide">
                    Notes internes
                    <textarea
                      maxLength={2000}
                      value={fields.notes ?? ''}
                      onChange={(event) => changeField('notes', event.currentTarget.value)}
                    />
                  </label>
                  {!editing && (
                    <label className="client-directory__wide">
                      Logo du client (facultatif, PNG/JPEG/WebP, 512 Ko maximum)
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(event) => setLogoFile(event.currentTarget.files?.[0] ?? null)}
                      />
                    </label>
                  )}
                </div>
                {source && (
                  <p className="client-directory__protect-note">
                    Les identifiants légaux viennent de Freebe ; les informations modifiables ici
                    restent propres à Tosho.
                  </p>
                )}
              </form>
            )}

            {wizard === 'confirm' && (
              <div className="client-directory__confirmation-step">
                <p className="client-directory__eyebrow">Relation</p>
                <h3>
                  {attachTarget || groupAttachTarget
                    ? 'Confirmer le rapprochement'
                    : linkedTarget
                      ? 'Confirmer la fiche Client existante'
                      : 'Confirmer la création du Client'}
                </h3>
                <p>
                  <strong>
                    {groupAttachTarget?.displayName ??
                      attachTarget?.displayName ??
                      fields.displayName}
                  </strong>
                  {groupAttachTarget
                    ? ' · fiche Client existante'
                    : source
                      ? ` · Freebe : ${source.displayName}`
                      : ' · création manuelle'}
                </p>
                <p>
                  La sélection d’une référence Freebe ne prouve pas qu’un devis a été accepté. La
                  confirmation de la relation Client est une décision de l’Administrateur.
                </p>
                {linkedTarget && (
                  <p>
                    La fiche existante sera conservée avec ses coordonnées, son logo et son
                    historique. Aucune nouvelle fiche ne sera créée.
                  </p>
                )}
                {prospectGroupId && (
                  <p>
                    Ce destinataire de devis sera lié à la fiche Client. Son devis conserve son état
                    historique.
                  </p>
                )}
                <label className="client-directory__confirmation">
                  <input
                    type="checkbox"
                    checked={relationshipConfirmed}
                    onChange={(event) => setRelationshipConfirmed(event.currentTarget.checked)}
                  />
                  Je confirme la relation Client et le rapprochement présenté.
                </label>
              </div>
            )}

            {wizard === 'logo' && editing && (
              <div className="client-directory__fields">
                <label>
                  Logo de {editing.displayName}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => setLogoFile(event.currentTarget.files?.[0] ?? null)}
                  />
                </label>
                <p>Le fichier reste privé dans Tosho.</p>
              </div>
            )}

            {review && (
              <div className="client-directory__review tosho-feedback is-error" role="alert">
                <h3>Rapprochement à vérifier</h3>
                <p>
                  Ces fiches peuvent représenter le même Client. Comparez-les avant de confirmer une
                  fiche distincte.
                </p>
                <ul>
                  {review.candidates.map((candidate) => (
                    <li key={candidate.id}>{candidate.displayName}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void review.execute(review.candidates.map((item) => item.id))}
                >
                  Je confirme une fiche distincte
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReview(null);
                    closeWizard();
                    openClients();
                  }}
                >
                  Rechercher une fiche existante
                </button>
              </div>
            )}
            <footer className="client-directory__modal-footer">
              <button
                type="button"
                onClick={() => {
                  if (wizard === 'confirm' && linkedTarget) {
                    setLinkedTarget(null);
                    setWizard('pick');
                  } else if (wizard === 'pick' && !attachTarget) setWizard('source');
                  else if (wizard === 'link' || (wizard === 'confirm' && groupAttachTarget)) {
                    setGroupAttachTarget(null);
                    setWizard(wizard === 'link' ? 'source' : 'link');
                  } else if (wizard === 'confirm' && !attachTarget) setWizard('identity');
                  else closeWizard();
                }}
              >
                {wizard === 'source' || editing ? 'Annuler' : 'Retour'}
              </button>
              {wizard === 'identity' && (
                <button
                  type="submit"
                  form="client-identity"
                  className="client-directory__primary"
                  disabled={busy}
                >
                  {editing ? 'Enregistrer' : 'Continuer'}
                </button>
              )}
              {wizard === 'confirm' && (
                <button
                  type="button"
                  className="client-directory__primary"
                  disabled={!relationshipConfirmed || busy}
                  onClick={save}
                >
                  {busy
                    ? 'Enregistrement…'
                    : attachTarget
                      ? 'Confirmer le rattachement'
                      : groupAttachTarget
                        ? 'Lier au Client'
                        : linkedTarget
                          ? 'Confirmer comme Client'
                          : 'Créer le Client'}
                </button>
              )}
              {wizard === 'logo' && editing && (
                <button
                  type="button"
                  className="client-directory__primary"
                  disabled={!logoFile || busy}
                  onClick={() => {
                    if (logoFile)
                      void run(() => clientDirectoryApi.saveLogo(editing, logoFile, csrfToken));
                  }}
                >
                  Enregistrer le logo
                </button>
              )}
            </footer>
          </section>
        </ModalBackdrop>
      )}
      {confirmation && (
        <ModalBackdrop
          className="client-directory__overlay client-directory"
          aria-labelledby="requalify-heading"
          close={<ModalCloseButton aria-label="Fermer" onClick={() => setConfirmation(null)} />}
        >
          <section className="tosho-modal-panel client-directory__modal client-directory__requalify-dialog">
            <header className="client-directory__modal-heading tosho-modal-header">
              <h2 id="requalify-heading">
                <PrototypeIcon name="user-round-help" /> Repasser en prospect
              </h2>
            </header>
            <p>
              {confirmation.length === 1
                ? confirmation[0]?.displayName
                : `${confirmation.length} clients sélectionnés`}
            </p>
            <p>
              La qualification sera corrigée. Les devis, factures et contacts déjà enregistrés
              restent liés et consultables dans leur historique.
            </p>
            <label className="client-directory__confirmation">
              <input
                type="checkbox"
                checked={confirmationChecked}
                onChange={(event) => setConfirmationChecked(event.currentTarget.checked)}
              />
              Je confirme cette correction.
            </label>
            <footer className="client-directory__modal-footer">
              <button type="button" onClick={() => setConfirmation(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="client-directory__primary"
                disabled={!confirmationChecked || busy}
                onClick={() => void requalify()}
              >
                {busy ? 'Correction…' : 'Confirmer'}
              </button>
            </footer>
          </section>
        </ModalBackdrop>
      )}
    </div>
  );
}

function Back({
  onClick,
  label = 'Retour au répertoire',
}: {
  onClick: () => void;
  label?: string;
}): React.JSX.Element {
  return (
    <button type="button" className="client-directory__back" onClick={onClick}>
      <PrototypeIcon name="arrow-left" /> {label}
    </button>
  );
}
function Search({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}): React.JSX.Element {
  return (
    <div className="client-directory__search" role="search">
      <PrototypeIcon name="search" />
      <input
        aria-label={placeholder}
        type="search"
        value={value}
        maxLength={120}
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
function ClientLogo({ client }: { client: ClientRecord }): React.JSX.Element {
  return (
    <span className="client-directory__logo-frame">
      {client.hasLogo ? (
        <img
          src={`/api/clients/${encodeURIComponent(client.id)}/logo?v=${client.version}`}
          alt={`Logo de ${client.displayName}`}
        />
      ) : (
        <span aria-hidden="true">{client.displayName.slice(0, 2).toLocaleUpperCase('fr-FR')}</span>
      )}
    </span>
  );
}
function ClientRows({
  clients,
  onOpen,
  selectedIds,
  onSelectionChange,
}: {
  clients: readonly ClientRecord[];
  onOpen: (client: ClientRecord) => void;
  selectedIds?: readonly string[];
  onSelectionChange?: (ids: readonly string[]) => void;
}): React.JSX.Element {
  return (
    <div
      className={`client-directory__table-wrap${onSelectionChange ? ' client-directory__table-wrap--selectable' : ''}`}
    >
      <table>
        <thead>
          <tr>
            {onSelectionChange && (
              <th>
                <input
                  type="checkbox"
                  aria-label="Sélectionner tous les clients affichés"
                  checked={
                    clients.length > 0 &&
                    clients.every((client) => selectedIds?.includes(client.id))
                  }
                  onChange={(event) =>
                    onSelectionChange(
                      event.currentTarget.checked ? clients.map((client) => client.id) : [],
                    )
                  }
                />
              </th>
            )}
            <th>Client</th>
            <th>Relation</th>
            <th>Projets</th>
            <th>SAV</th>
            <th>FAQ</th>
            <th>CA</th>
            <th>
              <span className="sr-only">Ouvrir</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr key={client.id}>
              {onSelectionChange && (
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Sélectionner ${client.displayName}`}
                    checked={selectedIds?.includes(client.id) ?? false}
                    onChange={(event) =>
                      onSelectionChange(
                        event.currentTarget.checked
                          ? [...(selectedIds ?? []), client.id]
                          : (selectedIds ?? []).filter((id) => id !== client.id),
                      )
                    }
                  />
                </td>
              )}
              <td>
                <button
                  type="button"
                  className="client-directory__row-link"
                  onClick={() => onOpen(client)}
                >
                  <ClientLogo client={client} />
                  <span>
                    <strong>{client.displayName}</strong>
                    <small>
                      {client.email ||
                        (client.sources.length ? 'Freebe confirmé' : 'Création manuelle')}
                    </small>
                  </span>
                </button>
              </td>
              <td>
                <span className="client-directory__badge">
                  {client.origin === 'FREEBE_LEGACY_UNREVIEWED' ? 'Lien à vérifier' : 'Client'}
                </span>
              </td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td aria-hidden="true">›</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
